import { useMemo, type VoidFunctionComponent } from 'react';
import styles from '../../ReportGenerationV2.module.less';
import SidebarNav from '../../../../../generic/SidebarNav';
import type { ReportCategory } from '../../../shared/types';
import { useCanDisplaySecurityOptions } from '../../../hooks';
import { useLocation, useParams } from '../../../../../../contexts/RouterContext';
import { FULL_SECURITY } from '../../../../../../../shared/featureFlags/flagNames';
import { useIsFeatureEnabled, useIsSecurityEnabled } from '../../../../../../helpers/features';
import { ReportCategories } from '../../utils';
import Locator from '../../../../../../../shared/Locator';
import { BinaryFetcherName } from '../../../../../../../shared/fetchers';
import { useReleaseGroupAllProjects } from '../../../../../../hooks/api/releaseGroups';

/**
 * Mini hook memoizing whether the Remiedation Guidance report should be shown based on
 * security feature flag and and the project(s) fetcher type(s).
 */
const useIsRemediationEnabled = () => {
  const isSecurityEnabled = useIsSecurityEnabled();

  // eslint-disable-next-line @typescript-eslint/naming-convention
  const params = useParams<{ projectGroupId: number | null, project_id: string | null }>();
  // This component is used for both project and release groups;
  // But we cannot call useReleaseGroupAllProjects non-deterministically
  const allProjectLocators = useReleaseGroupAllProjects(params.projectGroupId ?? 0).data ?? [];
  let areAllProjectsBinary = false;

  if (params.projectGroupId) { // 0 is not a valid projectGroupId anyway
    // release group case
    areAllProjectsBinary = allProjectLocators.every(loc => {
      const { fetcher } = Locator.parse(loc);
      return fetcher === BinaryFetcherName;
    });
  } else if (params.project_id) {
    // single project case
    const { fetcher } = Locator.parse(params.project_id);
    areAllProjectsBinary = fetcher === BinaryFetcherName;
  }

  return useMemo(() => {
    return isSecurityEnabled && !areAllProjectsBinary;
  }, [isSecurityEnabled, areAllProjectsBinary]);
};

type Props = {
  reportType: ReportCategory | 'settings';
  url: string;
  resetFormat: () => void;
};

const Sidebar: VoidFunctionComponent<Props> = ({ url, reportType, resetFormat }: Props) => {
  const shouldDisplayVulnReport = useCanDisplaySecurityOptions();
  const location = useLocation();
  const isRemediationGuidanceEnabled = useIsRemediationEnabled();
  const isSettingsActive = location.pathname.includes('settings');
  const isFullSecurityEnabled = useIsFeatureEnabled(FULL_SECURITY);

  return (
    <span className={styles.sidebar}>
      <SidebarNav>
        <SidebarNav.Heading>Reports</SidebarNav.Heading>
        <SidebarNav.Item
          onClick={resetFormat}
          isActive={reportType === ReportCategories.Licensing}
          to={`${url}/${ReportCategories.Licensing}`}
        >
          Licensing
        </SidebarNav.Item>
        <SidebarNav.Item
          onClick={resetFormat}
          isActive={reportType === ReportCategories.SBOM}
          to={`${url}/${ReportCategories.SBOM}`}>
          SBOM
        </SidebarNav.Item>
        {shouldDisplayVulnReport && (
          <SidebarNav.Item
            onClick={resetFormat}
            isActive={reportType === ReportCategories.Vulnerability}
            to={`${url}/${ReportCategories.Vulnerability}`}>
            Vulnerabilities
          </SidebarNav.Item>
        )}
        {isRemediationGuidanceEnabled && (
          <SidebarNav.PaidItem
            onClick={resetFormat}
            isActive={reportType === ReportCategories.RemediationGuidance}
            to={`${url}/${ReportCategories.RemediationGuidance}`}
            isRestricted={!isFullSecurityEnabled}
          >
            Remediation Guidance
          </SidebarNav.PaidItem>
        )}
        <SidebarNav.Item onClick={resetFormat} isActive={isSettingsActive} to={`${url}/settings`}>
          Settings
        </SidebarNav.Item>
      </SidebarNav>
    </span>
  );
};

export default Sidebar;
